import { useCallback, useContext, useRef } from 'react';
import { UseWebsocketResponse } from './models';
import { WebsocketContext, WebsocketData } from './context';
import { uuidv4 } from '../../util';

export function useWebsocket(): UseWebsocketResponse {
    const context = useContext<WebsocketData | undefined>(WebsocketContext);

    if (!context) {
        throw new Error(
            "Websocket context unavailable. Are you sure you're using this in a nested component of a WebsocketProvider?"
        );
    }

    const { connected, authenticated, socket, setSubscriptions } = context;

    const subscribe = useCallback(
        (subscriptions: Array<{ resource: string }>): string[] => {
            const newSubscriptions: {
                [id: string]: { resource: string; scheduleForRemoval: boolean };
            } = {};
            const ids: string[] = [];
            for (const s of subscriptions) {
                const id = uuidv4();
                ids.push(id);
                newSubscriptions[id] = {
                    resource: s.resource,
                    scheduleForRemoval: false
                };
            }

            setSubscriptions((current) => ({
                ...current,
                ...newSubscriptions
            }));

            return ids;
        },
        [setSubscriptions]
    );

    const unsubscribe = useCallback(
        (ids: string[]): void => {
            setSubscriptions((current) => {
                const newSubscriptions = { ...current };
                for (const id of ids) {
                    if (newSubscriptions[id]) {
                        newSubscriptions[id].scheduleForRemoval = true;
                    }
                }
                return newSubscriptions;
            });
        },
        [setSubscriptions]
    );

    const socketRef = useRef(socket);
    socketRef.current = socket;
    const on = useCallback(
        (event: string, callback: (message: unknown) => void): void => {
            socketRef.current.on(event, callback);
        },
        []
    );

    const off = useCallback(
        (event: string, callback: (message: unknown) => void): void => {
            socketRef.current.off(event, callback);
        },
        []
    );

    return { connected, authenticated, on, off, subscribe, unsubscribe };
}
